'use client';

import React from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import styled from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';
import { useLabels } from '@liftango/ui-tokens';
import { Icon } from '@liftango/ui-icons';

export interface PopoverProps {
  /** Open state. When provided the popover is controlled. */
  open?: boolean;
  /** Called when the open state should change. */
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export interface PopoverTriggerProps {
  children: React.ReactElement;
}

export interface PopoverContentProps {
  /** Side relative to the trigger. Defaults to `bottom`. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alignment along the cross axis. Defaults to `start`. */
  align?: 'start' | 'center' | 'end';
  /** Gap between trigger and content in px. Defaults to 4. */
  sideOffset?: number;
  /** Show a close button in the top-right corner. */
  showClose?: boolean;
  /** Max width in px. Defaults to 320. */
  maxWidth?: number;
  children: React.ReactNode;
}

const ContentBox = styled(RadixPopover.Content)<{ $maxWidth: number }>`
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.lg};
  box-shadow: ${(p) => p.theme.elevation.overlay.shadow};
  padding: ${(p) => p.theme.spaceAlias.md};
  z-index: ${(p) => p.theme.zIndex.popover};
  max-width: ${(p) => p.$maxWidth}px;
  outline: none;
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  color: ${(p) => p.theme.colors.neutral.ink};

  &[data-state='open'] {
    animation: popoverIn 120ms ease;
  }
  &[data-state='closed'] {
    animation: popoverOut 100ms ease;
  }

  @keyframes popoverIn {
    from {
      opacity: 0;
      transform: scale(0.97) translateY(-2px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  @keyframes popoverOut {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`;

const CloseBtn = styled(RadixPopover.Close)`
  position: absolute;
  top: ${(p) => p.theme.spaceAlias.sm};
  inset-inline-end: ${(p) => p.theme.spaceAlias.sm};
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  cursor: pointer;
  border-radius: ${(p) => p.theme.radius.sm};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${(p) => p.theme.colors.neutral.line};
  }
  ${focusRing}
`;

const ContentInner = styled.div<{ $hasClose: boolean }>`
  position: relative;
  padding-inline-end: ${(p) => (p.$hasClose ? '28px' : '0')};
`;

/**
 * Click-triggered positioned overlay. Use for context menus, filter panels,
 * overflow action lists, and other on-demand content anchored to a trigger.
 *
 * Uncontrolled by default. Pass `open` + `onOpenChange` for controlled mode.
 *
 * @example
 * <Popover>
 *   <Popover.Trigger><IconButton icon={<Icon name="ellipsis" />} label="More" /></Popover.Trigger>
 *   <Popover.Content side="bottom" align="end">
 *     <ActionList />
 *   </Popover.Content>
 * </Popover>
 */
export function Popover({ open, onOpenChange, children }: PopoverProps) {
  return (
    <RadixPopover.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </RadixPopover.Root>
  );
}

/**
 * The element that opens the popover on click. Must contain exactly one
 * interactive child (button, IconButton, etc.) — rendered via `asChild`.
 */
function Trigger({ children }: PopoverTriggerProps) {
  return <RadixPopover.Trigger asChild>{children}</RadixPopover.Trigger>;
}

/**
 * The popover panel. Portalled to `document.body` so it escapes overflow
 * and stacking contexts.
 */
function Content({
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  showClose = false,
  maxWidth = 320,
  children,
}: PopoverContentProps) {
  const labels = useLabels();
  const closeLabel = labels.actions?.close ?? 'Close';
  return (
    <RadixPopover.Portal>
      <ContentBox side={side} align={align} sideOffset={sideOffset} $maxWidth={maxWidth}>
        <ContentInner $hasClose={showClose}>
          {children}
          {showClose && (
            <CloseBtn aria-label={closeLabel}>
              <Icon name="xmark" />
            </CloseBtn>
          )}
        </ContentInner>
      </ContentBox>
    </RadixPopover.Portal>
  );
}

/**
 * Convenience re-export of the Radix anchor, for positioning a popover
 * relative to a different element than the trigger.
 */
const Anchor = RadixPopover.Anchor;

Popover.Trigger = Trigger;
Popover.Content = Content;
Popover.Anchor = Anchor;
